/**
 * Calculadora de tragos — Tost
 *
 * Calcula cuántas unidades/botellas comprar por categoría de bebida según:
 * invitados, duración del evento, tragos/persona/hora, % de abstemios y
 * margen de seguridad. Cada categoría se configura desde el editor de temas
 * (bloques "categoria"), incluyendo la colección y el producto de Tost que
 * se enlazan/agregan al carrito.
 */
if (!customElements.get('drink-calculator')) {
  const EVENT_PRESETS = {
    wedding: { hours: 5, drinksPerHour: 1, nonDrinker: 20 },
    birthday: { hours: 4, drinksPerHour: 1.5, nonDrinker: 15 },
    party: { hours: 3, drinksPerHour: 2, nonDrinker: 10 },
    corporate: { hours: 2, drinksPerHour: 1, nonDrinker: 25 }
  };

  class DrinkCalculator extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('.js-dc-form') || this.querySelector('form');
      this.guestsInput = this.querySelector('.js-dc-guests');
      this.hoursInput = this.querySelector('.js-dc-hours');
      this.eventTypeSelect = this.querySelector('.js-dc-event-type');
      this.drinksPerHourInput = this.querySelector('.js-dc-drinks-per-hour');
      this.nonDrinkerInput = this.querySelector('.js-dc-non-drinker');
      this.marginInput = this.querySelector('.js-dc-margin');
      this.calculateBtn = this.querySelector('.js-dc-calculate');
      this.addAllBtn = this.querySelector('.js-dc-add-all');
      this.addAllMessage = this.querySelector('.js-dc-add-all-message');
      this.resultsSection = this.querySelector('.drink-calculator__results');
      this.resultsBody = this.querySelector('.js-dc-results-body');
      this.summaryEl = this.querySelector('.js-dc-summary');
      this.summaryTextEl = this.querySelector('.js-dc-summary-text');
      this.errorsEl = this.querySelector('.js-dc-errors');
      this.errorsTextEl = this.querySelector('.js-dc-errors-text');

      this.discountEnabled = this.dataset.discountEnabled === 'true';
      this.discountCode = (this.dataset.discountCode || '').trim();

      this.categories = Array.from(this.querySelectorAll('[data-category]')).map((el) => ({
        name: el.dataset.name || '',
        mixPct: Number(el.dataset.mixPct) || 0,
        servings: Number(el.dataset.servings) > 0 ? Number(el.dataset.servings) : 1,
        collectionUrl: el.dataset.collectionUrl || '',
        productUrl: el.dataset.productUrl || '',
        productName: el.dataset.productName || '',
        productImage: el.dataset.productImage || '',
        variantId: el.dataset.variantId || '',
        variantAvailable: el.dataset.variantAvailable === 'true',
        defaultIncluded: el.dataset.defaultIncluded === 'true'
      }));

      if (this.form) {
        this.form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          this.calculate();
        });
      }

      if (this.eventTypeSelect) {
        this.eventTypeSelect.addEventListener('change', this.applyEventPreset.bind(this));
        // Apply the initially selected preset so inputs start in sync.
        this.applyEventPreset();
      }

      if (this.addAllBtn) {
        this.addAllBtn.addEventListener('click', this.handleAddAll.bind(this));
      }
    }

    /**
     * Pre-fills hours / drinks-per-hour / non-drinker % based on the chosen
     * event type. Leaves everything untouched when "custom" is selected.
     */
    applyEventPreset() {
      const preset = EVENT_PRESETS[this.eventTypeSelect.value];
      if (!preset) return;

      if (this.hoursInput) this.hoursInput.value = preset.hours;
      if (this.drinksPerHourInput) this.drinksPerHourInput.value = preset.drinksPerHour;
      if (this.nonDrinkerInput) this.nonDrinkerInput.value = preset.nonDrinker;
    }

    /**
     * Reads the form inputs, computes bottles/units needed per category and
     * renders the results table.
     */
    calculate() {
      this.hideErrors();

      const guests = Number(this.guestsInput && this.guestsInput.value);
      const hours = Number(this.hoursInput && this.hoursInput.value);
      const drinksPerHour = Number(this.drinksPerHourInput && this.drinksPerHourInput.value);
      const nonDrinkerPct = Number(this.nonDrinkerInput && this.nonDrinkerInput.value) || 0;
      const marginPct = Number(this.marginInput && this.marginInput.value) || 0;

      if (!guests || guests <= 0) {
        this.showError('Ingresa un número de invitados válido.');
        return;
      }

      if (!hours || hours <= 0) {
        this.showError('Ingresa una duración del evento válida.');
        return;
      }

      const drinkingGuests = guests * (1 - Math.min(Math.max(nonDrinkerPct, 0), 100) / 100);
      const totalDrinks = drinkingGuests * hours * drinksPerHour * (1 + Math.max(marginPct, 0) / 100);

      const sumMix = this.categories.reduce((sum, cat) => sum + cat.mixPct, 0);

      this.results = this.categories.map((cat) => {
        const share = sumMix > 0 ? cat.mixPct / sumMix : 1 / this.categories.length;
        const drinksForCategory = totalDrinks * share;
        const units = Math.ceil(drinksForCategory / cat.servings);

        return {
          ...cat,
          drinks: drinksForCategory,
          units
        };
      });

      this.renderResults(totalDrinks);
    }

    /**
     * Renders the summary line + results table rows.
     * @param {number} totalDrinks - Total estimated drinks across all categories.
     */
    renderResults(totalDrinks) {
      if (this.summaryTextEl) {
        this.summaryTextEl.textContent = `Estimamos un total de ${this.formatNumber(totalDrinks, 0)} tragos para tu evento.`;
      }

      if (this.resultsBody) {
        this.resultsBody.innerHTML = '';

        this.results.forEach((row, index) => {
          const tr = document.createElement('tr');
          tr.className = 'drink-calculator__row';

          const nameTd = document.createElement('td');
          nameTd.setAttribute('data-label', 'Categoría');
          nameTd.textContent = row.name;

          const productTd = document.createElement('td');
          productTd.setAttribute('data-label', 'Producto sugerido');
          if (row.productName) {
            const productWrap = document.createElement(row.productUrl ? 'a' : 'div');
            productWrap.className = 'drink-calculator__product';
            if (row.productUrl) productWrap.href = row.productUrl;

            if (row.productImage) {
              const img = document.createElement('img');
              img.src = row.productImage;
              img.alt = row.productName;
              img.loading = 'lazy';
              img.width = 40;
              img.height = 40;
              img.className = 'drink-calculator__product-image';
              productWrap.appendChild(img);
            }

            const productInfo = document.createElement('div');
            productInfo.className = 'drink-calculator__product-info';

            const productName = document.createElement('span');
            productName.className = 'drink-calculator__product-name';
            productName.textContent = row.productName;
            productInfo.appendChild(productName);

            const productBadge = document.createElement('span');
            productBadge.className = 'drink-calculator__product-badge';
            productBadge.textContent = 'Sugerencia Tost';
            productInfo.appendChild(productBadge);

            productWrap.appendChild(productInfo);
            productTd.appendChild(productWrap);
          } else {
            productTd.textContent = 'Sin producto asignado';
          }

          const drinksTd = document.createElement('td');
          drinksTd.setAttribute('data-label', 'Tragos estimados');
          drinksTd.textContent = this.formatNumber(row.drinks, 0);

          const unitsTd = document.createElement('td');
          unitsTd.setAttribute('data-label', 'Unidades a comprar');

          const unitsInput = document.createElement('input');
          unitsInput.type = 'number';
          unitsInput.min = '0';
          unitsInput.step = '1';
          unitsInput.className = 'input drink-calculator__qty js-dc-qty';
          unitsInput.value = row.units;
          unitsInput.setAttribute('aria-label', `Unidades de ${row.name}`);
          unitsInput.dataset.index = index;
          unitsTd.appendChild(unitsInput);

          const buyTd = document.createElement('td');
          buyTd.setAttribute('data-label', 'Ver alternativas');
          if (row.collectionUrl) {
            const link = document.createElement('a');
            link.href = row.collectionUrl;
            link.className = 'link';
            link.textContent = 'Ver alternativas';
            buyTd.appendChild(link);
          } else {
            buyTd.textContent = '—';
          }

          const includeTd = document.createElement('td');
          includeTd.setAttribute('data-label', 'Agregar');
          const includeInput = document.createElement('input');
          includeInput.type = 'checkbox';
          includeInput.className = 'js-dc-include';
          includeInput.dataset.index = index;
          includeInput.checked = row.defaultIncluded && row.variantAvailable && !!row.variantId;
          includeInput.disabled = !row.variantId || !row.variantAvailable;
          includeInput.setAttribute('aria-label', `Agregar ${row.name} al carrito`);
          includeTd.appendChild(includeInput);
          if (!row.variantId || !row.variantAvailable) {
            const note = document.createElement('span');
            note.className = 'drink-calculator__note';
            note.textContent = row.variantId ? ' Sin stock' : ' Sin producto asignado';
            includeTd.appendChild(note);
          }

          tr.appendChild(nameTd);
          tr.appendChild(productTd);
          tr.appendChild(drinksTd);
          tr.appendChild(unitsTd);
          tr.appendChild(buyTd);
          tr.appendChild(includeTd);

          this.resultsBody.appendChild(tr);
        });
      }

      if (this.resultsSection) this.resultsSection.hidden = false;
      if (this.addAllMessage) this.addAllMessage.hidden = true;
    }

    /**
     * Collects checked rows (with an assigned, available variant and qty > 0)
     * and adds them to the cart in a single request, reusing the theme's
     * cart drawer / cart icon update flow when available.
     */
    async handleAddAll() {
      if (!this.results || !this.results.length) return;

      const qtyInputs = Array.from(this.querySelectorAll('.js-dc-qty'));
      const includeInputs = Array.from(this.querySelectorAll('.js-dc-include'));

      const items = [];
      includeInputs.forEach((checkbox) => {
        if (!checkbox.checked) return;
        const index = Number(checkbox.dataset.index);
        const row = this.results[index];
        const qtyInput = qtyInputs.find((input) => Number(input.dataset.index) === index);
        const quantity = qtyInput ? Number(qtyInput.value) : row.units;

        if (row && row.variantId && quantity > 0) {
          items.push({ id: Number(row.variantId), quantity });
        }
      });

      if (!items.length) {
        this.showError('Selecciona al menos una categoría con producto disponible para agregar al carrito.');
        return;
      }

      this.hideErrors();
      this.addAllBtn.disabled = true;
      this.addAllBtn.classList.add('is-loading');

      const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd)
        || `${(window.theme && window.theme.routes && window.theme.routes.cart) || '/cart'}/add.js`;
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer && cartDrawer.closest('.shopify-section')) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          const message = (data && (data.description || data.message)) || 'No pudimos agregar los productos al carrito.';
          throw new Error(message);
        }

        // If a discount coupon is configured, the only reliable way to apply a
        // Shopify discount code from the storefront is a real navigation to
        // /discount/<code>, which sets the discount before landing on the cart.
        // This means the cart drawer preview is skipped in favor of going
        // straight to the cart page with the coupon already applied.
        if (this.discountEnabled && this.discountCode) {
          window.location.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(cartUrl)}`;
          return;
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, true);
        } else {
          window.location.href = cartUrl;
          return;
        }

        if (this.addAllMessage) {
          this.addAllMessage.hidden = false;
          this.addAllMessage.textContent = '¡Productos agregados al carrito!';
        }
      } catch (error) {
        this.showError(typeof error === 'string' ? error : error.message);
      } finally {
        this.addAllBtn.disabled = false;
        this.addAllBtn.classList.remove('is-loading');
      }
    }

    /**
     * @param {number} value
     * @param {number} decimals
     * @returns {string} Locale-formatted number (es-CL grouping).
     */
    formatNumber(value, decimals) {
      return Number(value || 0).toLocaleString('es-CL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }

    showError(message) {
      if (!this.errorsEl) return;
      this.errorsTextEl.textContent = message;
      this.errorsEl.hidden = false;
    }

    hideErrors() {
      if (!this.errorsEl) return;
      this.errorsEl.hidden = true;
    }
  }

  customElements.define('drink-calculator', DrinkCalculator);
}
